import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Chip, IconButton, Tooltip, Menu, MenuItem, Snackbar, Alert } from '@mui/material';
import { CyberSpinner } from './CyberSpinner';
import { Well } from '../types';
import { useAppStore } from '@stores/appStore';
import { getCyberColors } from '../theme/cyberColors';
import MapIcon from '@mui/icons-material/Map';
import ShareIcon from '@mui/icons-material/Share';
import GoogleIcon from '@mui/icons-material/Google';
// Apple Maps and Waze removed - only Google Maps supports waypoints
import CloseIcon from '@mui/icons-material/Close';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearAllIcon from '@mui/icons-material/ClearAll';
// MUI Icons for facility types - matching quick search buttons
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WaterIcon from '@mui/icons-material/Water';
import ConstructionIcon from '@mui/icons-material/Construction';
import BoltIcon from '@mui/icons-material/Bolt';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import SettingsIcon from '@mui/icons-material/Settings';
import FactoryIcon from '@mui/icons-material/Factory';
import OpacityIcon from '@mui/icons-material/Opacity';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import ScienceIcon from '@mui/icons-material/Science';
import AdjustIcon from '@mui/icons-material/Adjust';
import HubIcon from '@mui/icons-material/Hub';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import PropaneTankIcon from '@mui/icons-material/PropaneTank';
import LandscapeIcon from '@mui/icons-material/Landscape';
import SpeedIcon from '@mui/icons-material/Speed';
import AirIcon from '@mui/icons-material/Air';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import DeleteIcon from '@mui/icons-material/Delete';
import RecyclingIcon from '@mui/icons-material/Recycling';
import HealingIcon from '@mui/icons-material/Healing';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import EngineeringIcon from '@mui/icons-material/Engineering';
import VerifiedIcon from '@mui/icons-material/Verified';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { telegram } from '../services/telegram';
import { PhotoGallery } from './PhotoGallery';
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { List, useDynamicRowHeight, useListRef } from 'react-window';
import { useTranslation } from '../i18n/translations';

// Constants for virtualization
const ITEM_HEIGHT = 196; // Default guess for virtualization; actual rows resize dynamically

// Status color mapping for cyberpunk theme - each status gets unique colors
const getStatusColors = (status: string) => {
  const s = status?.toLowerCase() || '';
  // Active/Producing - Green
  if (s.includes('active') || s.includes('producing') || s.includes('drilling') || s.includes('completed')) {
    return { bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.5)', hoverBg: 'rgba(16, 185, 129, 0.25)', hoverBorder: 'rgba(16, 185, 129, 0.8)', glow: '0 0 15px rgba(16, 185, 129, 0.25)', dot: '#10b981', text: '#34d399', dotGlow: '0 0 6px #10b981' };
  }
  // New/Permit - Cyan
  if (s.includes('new') || s.includes('permit') || s.includes('approved') || s.includes('pending')) {
    return { bg: 'rgba(6, 182, 212, 0.15)', border: 'rgba(6, 182, 212, 0.5)', hoverBg: 'rgba(6, 182, 212, 0.25)', hoverBorder: 'rgba(6, 182, 212, 0.8)', glow: '0 0 15px rgba(6, 182, 212, 0.25)', dot: '#06b6d4', text: '#22d3ee', dotGlow: '0 0 6px #06b6d4' };
  }
  // Inactive/Shut-in - Amber
  if (s.includes('inactive') || s.includes('shut') || s.includes('suspended') || s.includes('idle')) {
    return { bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.5)', hoverBg: 'rgba(245, 158, 11, 0.25)', hoverBorder: 'rgba(245, 158, 11, 0.8)', glow: '0 0 15px rgba(245, 158, 11, 0.25)', dot: '#f59e0b', text: '#fbbf24', dotGlow: '0 0 6px #f59e0b' };
  }
  // Plugged/Abandoned - Red
  if (s.includes('plugged') || s.includes('abandoned') || s.includes('p&a') || s.includes('ta')) {
    return { bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.5)', hoverBg: 'rgba(239, 68, 68, 0.25)', hoverBorder: 'rgba(239, 68, 68, 0.8)', glow: '0 0 15px rgba(239, 68, 68, 0.25)', dot: '#ef4444', text: '#f87171', dotGlow: '0 0 6px #ef4444' };
  }
  // Cancelled/Denied - Purple
  if (s.includes('cancel') || s.includes('denied') || s.includes('expired') || s.includes('void')) {
    return { bg: 'rgba(168, 85, 247, 0.15)', border: 'rgba(168, 85, 247, 0.5)', hoverBg: 'rgba(168, 85, 247, 0.25)', hoverBorder: 'rgba(168, 85, 247, 0.8)', glow: '0 0 15px rgba(168, 85, 247, 0.25)', dot: '#a855f7', text: '#c084fc', dotGlow: '0 0 6px #a855f7' };
  }
  // Default - Blue/Gray
  return { bg: 'rgba(96, 165, 250, 0.10)', border: 'rgba(96, 165, 250, 0.30)', hoverBg: 'rgba(96, 165, 250, 0.20)', hoverBorder: 'rgba(96, 165, 250, 0.60)', glow: '0 0 15px rgba(96, 165, 250, 0.15)', dot: '#94a3b8', text: '#94a3b8', dotGlow: 'none' };
};

// Type for grouped data - either a section header or a well
type ListItemData = { type: 'header'; letter: string; count: number } | { type: 'well'; well: Well };

type LetterStage = 'first' | 'second' | 'third' | 'fourth';
const letterStages: LetterStage[] = ['first', 'second', 'third', 'fourth'];

// Declare Telegram custom emoji element for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'tg-emoji': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        'emoji-id'?: string;
      };
    }
  }
}

// MUI Icon mapping for facility types - matches quick search buttons exactly
const FACILITY_MUI_ICONS: Record<string, { icon: React.ComponentType<any>; color: string }> = {
  // Main types
  well: { icon: LocalGasStationIcon, color: '#f59e0b' },
  wells: { icon: LocalGasStationIcon, color: '#f59e0b' },
  swd: { icon: WaterIcon, color: '#10b981' },
  swds: { icon: WaterIcon, color: '#10b981' },
  rig: { icon: ConstructionIcon, color: '#fbbf24' },
  rigs: { icon: ConstructionIcon, color: '#fbbf24' },
  // Tank & Storage
  tb: { icon: BoltIcon, color: '#06b6d4' },
  tankbattery: { icon: BoltIcon, color: '#06b6d4' },
  gs: { icon: PropaneTankIcon, color: '#fbbf24' },
  gasstorage: { icon: PropaneTankIcon, color: '#fbbf24' },
  bgt: { icon: LandscapeIcon, color: '#78716c' },
  belowgradetank: { icon: LandscapeIcon, color: '#78716c' },
  // Flare & Emissions
  fs: { icon: WhatshotIcon, color: '#ef4444' },
  flarestack: { icon: WhatshotIcon, color: '#ef4444' },
  // Compressor & Pumps
  cs: { icon: SettingsIcon, color: '#60a5fa' },
  compressor: { icon: SettingsIcon, color: '#60a5fa' },
  cp: { icon: AirIcon, color: '#64748b' },
  crudepump: { icon: AirIcon, color: '#64748b' },
  // Texas well type facets
  'tx-horizontal': { icon: TrendingFlatIcon, color: '#06b6d4' },
  'tx-directional': { icon: NorthEastIcon, color: '#a855f7' },
  'tx-oil': { icon: OilBarrelIcon, color: '#f59e0b' },
  'tx-gas': { icon: LocalFireDepartmentIcon, color: '#ef4444' },
  'tx-oil-gas': { icon: LocalGasStationIcon, color: '#fbbf24' },
  // Plants & Processing
  gp: { icon: FactoryIcon, color: '#a855f7' },
  gasplant: { icon: FactoryIcon, color: '#a855f7' },
  rf: { icon: OilBarrelIcon, color: '#a855f7' },
  refinery: { icon: OilBarrelIcon, color: '#a855f7' },
  tp: { icon: ScienceIcon, color: '#f472b6' },
  treatingplant: { icon: ScienceIcon, color: '#f472b6' },
  inj: { icon: MedicationLiquidIcon, color: '#34d399' },
  injectionplant: { icon: MedicationLiquidIcon, color: '#34d399' },
  // Pipelines
  plg: { icon: LinearScaleIcon, color: '#3b82f6' },
  gaspipeline: { icon: LinearScaleIcon, color: '#3b82f6' },
  plc: { icon: LinearScaleIcon, color: '#64748b' },
  crudepipeline: { icon: LinearScaleIcon, color: '#64748b' },
  plw: { icon: LinearScaleIcon, color: '#0ea5e9' },
  waterpipeline: { icon: LinearScaleIcon, color: '#0ea5e9' },
  // Gathering & Metering
  ggs: { icon: HubIcon, color: '#3b82f6' },
  gasgathering: { icon: HubIcon, color: '#3b82f6' },
  mgs: { icon: SpeedIcon, color: '#8b5cf6' },
  meteringstation: { icon: SpeedIcon, color: '#8b5cf6' },
  wh: { icon: AdjustIcon, color: '#f59e0b' },
  wellhead: { icon: AdjustIcon, color: '#f59e0b' },
  wellpad: { icon: AdjustIcon, color: '#f59e0b' },
  // Brine Well
  bw: { icon: OpacityIcon, color: '#0ea5e9' },
  brinewell: { icon: OpacityIcon, color: '#0ea5e9' },
  // Waste & Environmental
  pit: { icon: LandscapeIcon, color: '#78716c' },
  pits: { icon: LandscapeIcon, color: '#78716c' },
  wm: { icon: DeleteIcon, color: '#ef4444' },
  wastemgmt: { icon: DeleteIcon, color: '#ef4444' },
  rfl: { icon: RecyclingIcon, color: '#22c55e' },
  recycling: { icon: RecyclingIcon, color: '#22c55e' },
  rm: { icon: HealingIcon, color: '#eab308' },
  remediate: { icon: HealingIcon, color: '#eab308' },
  ab: { icon: CleaningServicesIcon, color: '#f97316' },
  abatement: { icon: CleaningServicesIcon, color: '#f97316' },
  // Service & Regulatory
  sc: { icon: EngineeringIcon, color: '#64748b' },
  serviceco: { icon: EngineeringIcon, color: '#64748b' },
  ci: { icon: VerifiedIcon, color: '#34d399' },
  c2: { icon: VerifiedIcon, color: '#34d399' },
  classiii: { icon: VerifiedIcon, color: '#34d399' },
  // Default
  facility: { icon: LocalGasStationIcon, color: '#f59e0b' },
  default: { icon: LocalGasStationIcon, color: '#f59e0b' }
};

// Helper function to get facility type color for card styling
function getFacilityTypeColor(type: string, facilityType?: string, name?: string): string {
  let iconType = (type || 'well').toLowerCase();
  
  if (type === 'facility' && facilityType) {
    const match = facilityType.match(/\(([A-Z0-9]+)\)/);
    if (match) {
      iconType = match[1].toLowerCase();
    } else {
      iconType = facilityType.toLowerCase().replace(/[\s_-]/g, '');
    }
  }
  
  // Fallback detection from name
  if (iconType === 'facility' || iconType === 'default' || iconType === 'well') {
    const upperName = (name || '').toUpperCase();
    if (upperName.includes('PIT') || upperName.includes('PITS')) iconType = 'pit';
    else if (upperName.includes('TANK') || upperName.includes('BATTERY')) iconType = 'tb';
    else if (upperName.includes('FLARE')) iconType = 'fs';
    else if (upperName.includes('COMPRESSOR')) iconType = 'cs';
    else if (upperName.includes('SWD') || upperName.includes('DISPOSAL')) iconType = 'swd';
    else if (upperName.includes('BRINE')) iconType = 'bw';
  }
  
  return (FACILITY_MUI_ICONS[iconType] || FACILITY_MUI_ICONS.default).color;
}

// Component to render facility icon using MUI icons (matching FavoritesPanel style with circular background)
function FacilityIcon({ type, facilityType, name, size = 36 }: { type: string; facilityType?: string; name?: string; size?: number }) {
  // For facilities with specific subtypes like "Brine Well - (BW)", extract the code
  let iconType = (type || 'well').toLowerCase();
  
  if (type === 'facility' && facilityType) {
    // Try to extract code from "Brine Well - (BW)" format
    const match = facilityType.match(/\(([A-Z0-9]+)\)/);
    if (match) {
      iconType = match[1].toLowerCase(); // 'bw', 'tb', 'fs', etc.
    } else {
      // Try matching by name like "Brine Well" -> "brinewell"
      iconType = facilityType.toLowerCase().replace(/[\s_-]/g, '');
    }
  }
  
  // Fallback: detect facility type from name if facilityType not set or icon still default
  if (iconType === 'facility' || iconType === 'default' || iconType === 'well') {
    const upperName = (name || '').toUpperCase();
    if (upperName.includes('PIT') || upperName.includes('PITS')) {
      iconType = 'pit';
    } else if (upperName.includes('TANK') || upperName.includes('BATTERY')) {
      iconType = 'tb';
    } else if (upperName.includes('FLARE')) {
      iconType = 'fs';
    } else if (upperName.includes('COMPRESSOR')) {
      iconType = 'cs';
    } else if (upperName.includes('SWD') || upperName.includes('DISPOSAL')) {
      iconType = 'swd';
    } else if (upperName.includes('BRINE')) {
      iconType = 'bw';
    }
  }
  
  const iconData = FACILITY_MUI_ICONS[iconType] || FACILITY_MUI_ICONS.default;
  const Icon = iconData.icon;
  
  // Circular icon with background - matching FavoritesPanel style
  return (
    <Box
      sx={{
        width: size + 8,
        height: size + 8,
        minWidth: size + 8,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${iconData.color}20`,
        border: `2px solid ${iconData.color}`,
        flexShrink: 0,
        boxShadow: `0 0 12px ${iconData.color}30`
      }}
    >
      <Icon sx={{ fontSize: size * 0.6, color: iconData.color }} />
    </Box>
  );
}

function getWellNameGeneric(well: Well | any): string {
  return well?.wellName || well?.name || '';
}

function getLetterKey(name: string): string {
  const firstChar = (name || '').charAt(0).toUpperCase();
  return /[A-Z]/.test(firstChar) ? firstChar : '#';
}

interface SearchResultsProps {
  open: boolean;
  wells: Well[];
  query: string;
  sortOrder: 'asc' | 'desc';
  facilityTypeLabel?: string; // Optional label for facility type filter
  cacheInfo?: { count: number; time: number; cached?: boolean } | null; // Load info: { count, time in ms, cached flag }
  loading: boolean;
  // Pagination props for infinite scroll
  totalCount?: number; // Total count of all facilities matching the query
  hasMore?: boolean; // Whether there are more results to load
  loadingMore?: boolean; // Whether currently loading more results
  onLoadMore?: () => void; // Callback to load more results
  onClose: () => void;
  onSelectWell: (well: Well) => void;
  onViewOnMap: (well: Well) => void;
  onToggleSort: () => void;
  onToggleFavorite: (well: Well) => void;
}

export function SearchResults({ open, wells, query, sortOrder, facilityTypeLabel, cacheInfo, loading, totalCount, hasMore, loadingMore, onLoadMore, onClose, onSelectWell, onViewOnMap, onToggleSort, onToggleFavorite }: SearchResultsProps) {
  const isFavorite = useAppStore((state) => state.isFavorite);
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((s) => s.highContrast);
  const cyber = getCyberColors(darkMode, highContrast);
  const { t } = useTranslation();
  const tr = useCallback((key: string, fallback: string) => {
      const fn = t as unknown as (k: string) => string | undefined;
    return fn(key) || fallback;
  }, [t]);
  const listRef = useListRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [listHeight, setListHeight] = useState(500);
  
  // Current letter being viewed (for floating indicator)
  const [currentLetter, setCurrentLetter] = useState<string>('');
  const [showLetterBadge, setShowLetterBadge] = useState(false);
  const badgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countyFilter, setCountyFilter] = useState<string>('all');
  const [operatorFilter, setOperatorFilter] = useState<string>('all');
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [countyAnchorEl, setCountyAnchorEl] = useState<null | HTMLElement>(null);
  const [operatorAnchorEl, setOperatorAnchorEl] = useState<null | HTMLElement>(null);
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [letterCounts, setLetterCounts] = useState<Record<string, number>>({});
  const [secondCounts, setSecondCounts] = useState<Record<string, Record<string, number>>>({});
  const [thirdCounts, setThirdCounts] = useState<Record<string, Record<string, Record<string, number>>>>({});
  const [fourthCounts, setFourthCounts] = useState<Record<string, Record<string, Record<string, Record<string, number>>>>>({});
  const [availableLetters, setAvailableLetters] = useState<string[]>([]);
  const [letterStage, setLetterStage] = useState<LetterStage>('first');
  const [letterExpanded, setLetterExpanded] = useState<boolean>(false);
  const [selectedLetter, setSelectedLetter] = useState<string>('');
  const [availableSecondLetters, setAvailableSecondLetters] = useState<string[]>([]);
  const [selectedSecondLetter, setSelectedSecondLetter] = useState<string>('');
  const [availableThirdLetters, setAvailableThirdLetters] = useState<string[]>([]);
  const [selectedThirdLetter, setSelectedThirdLetter] = useState<string>('');
  const [availableFourthLetters, setAvailableFourthLetters] = useState<string[]>([]);
  const [selectedFourthLetter, setSelectedFourthLetter] = useState<string>('');
  
  // Photo Gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryFacility, setGalleryFacility] = useState<Well | null>(null);
  
  const handleOpenGallery = useCallback((well: Well, e: React.MouseEvent) => {
    e.stopPropagation();
    telegram.hapticFeedback('light');
    setGalleryFacility(well);
    setGalleryOpen(true);
  }, []);
  
  // Calculate list height on mount, resize, and data load
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        setListHeight(containerRef.current.clientHeight);
      }
    };
    // Small delay to allow DOM to settle after render
    const timer = setTimeout(updateHeight, 50);
    window.addEventListener('resize', updateHeight);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateHeight);
    };
  }, [open, loading, wells.length]);
  
  // Determine what type of facilities we're searching for
  const facilityTypeName = facilityTypeLabel && facilityTypeLabel !== 'Show All' 
    ? facilityTypeLabel.toLowerCase() 
    : 'facilities';
  const sortLetters = useCallback((letters: string[]) => {
    return [...letters].sort((a, b) => {
      if (a === '#') return -1;
      if (b === '#') return 1;
      return a.localeCompare(b);
    });
  }, []);
  
  // Apply status/county/operator filters
  const filteredByMeta = useMemo(() => {
    return wells.filter(well => {
      const statusMatch = statusFilter === 'all' || well.status?.toLowerCase() === statusFilter.toLowerCase();
      const countyMatch = countyFilter === 'all' || well.county?.toLowerCase() === countyFilter.toLowerCase();
      const operatorMatch = operatorFilter === 'all' || well.operator?.toLowerCase() === operatorFilter.toLowerCase();
      return statusMatch && countyMatch && operatorMatch;
    });
  }, [wells, statusFilter, countyFilter, operatorFilter]);
  
  // Compute letter drilldown counts
  const letterStats = useMemo(() => {
    const lCounts: Record<string, number> = {};
    const sCounts: Record<string, Record<string, number>> = {};
    const tCounts: Record<string, Record<string, Record<string, number>>> = {};
    const fCounts: Record<string, Record<string, Record<string, Record<string, number>>>> = {};

    filteredByMeta.forEach((well) => {
      const name = getWellNameGeneric(well);
      const letter = getLetterKey(name);
      lCounts[letter] = (lCounts[letter] || 0) + 1;

      if (letter === '#') return;

      const secondChar = name.charAt(1);
      if (secondChar) {
        const second = getLetterKey(secondChar);
        sCounts[letter] = sCounts[letter] || {};
        sCounts[letter][second] = (sCounts[letter][second] || 0) + 1;

        const thirdChar = name.charAt(2);
        if (thirdChar) {
          const third = getLetterKey(thirdChar);
          tCounts[letter] = tCounts[letter] || {};
          tCounts[letter][second] = tCounts[letter][second] || {};
          tCounts[letter][second][third] = (tCounts[letter][second][third] || 0) + 1;

          const fourthChar = name.charAt(3);
          if (fourthChar) {
            const fourth = getLetterKey(fourthChar);
            fCounts[letter] = fCounts[letter] || {};
            fCounts[letter][second] = fCounts[letter][second] || {};
            fCounts[letter][second][third] = fCounts[letter][second][third] || {};
            fCounts[letter][second][third][fourth] = (fCounts[letter][second][third][fourth] || 0) + 1;
          }
        }
      }
    });

    return { lCounts, sCounts, tCounts, fCounts };
  }, [filteredByMeta]);
  
  useEffect(() => {
    setLetterCounts(letterStats.lCounts);
    setSecondCounts(letterStats.sCounts);
    setThirdCounts(letterStats.tCounts);
    setFourthCounts(letterStats.fCounts);

    const letters = Object.keys(letterStats.lCounts || {});
    setAvailableLetters(sortLetters(letters));
  }, [letterStats, sortLetters]);
  
  useEffect(() => {
    // Keep drilldown on "All" by default; only reset selection if it becomes invalid
    if (selectedLetter && availableLetters.length > 0 && !availableLetters.includes(selectedLetter)) {
      setSelectedLetter('');
      setSelectedSecondLetter('');
      setSelectedThirdLetter('');
      setSelectedFourthLetter('');
      setLetterStage('first');
    }
    if (availableLetters.length === 0) {
      setSelectedLetter('');
      setSelectedSecondLetter('');
      setSelectedThirdLetter('');
      setSelectedFourthLetter('');
      setLetterStage('first');
    }
  }, [availableLetters, selectedLetter]);

  const maxLetterStageIndex = useMemo(() => {
    if (selectedLetter && selectedSecondLetter && selectedThirdLetter) return 3;
    if (selectedLetter && selectedSecondLetter) return 2;
    if (selectedLetter) return 1;
    return 0;
  }, [selectedLetter, selectedSecondLetter, selectedThirdLetter]);

  useEffect(() => {
    const currentIndex = letterStages.indexOf(letterStage);
    if (currentIndex > maxLetterStageIndex) {
      setLetterStage(letterStages[maxLetterStageIndex]);
    }
  }, [letterStage, maxLetterStageIndex]);
  
  useEffect(() => {
    if (!selectedLetter) {
      setAvailableSecondLetters([]);
      setSelectedSecondLetter('');
      setAvailableThirdLetters([]);
      setSelectedThirdLetter('');
      setAvailableFourthLetters([]);
      setSelectedFourthLetter('');
      return;
    }

    const seconds = sortLetters(Object.keys(secondCounts[selectedLetter] || {}));
    setAvailableSecondLetters(seconds);
    if (selectedSecondLetter && !seconds.includes(selectedSecondLetter)) {
      setSelectedSecondLetter('');
      setAvailableThirdLetters([]);
      setSelectedThirdLetter('');
      setAvailableFourthLetters([]);
      setSelectedFourthLetter('');
    }
  }, [selectedLetter, secondCounts, selectedSecondLetter, sortLetters]);

  useEffect(() => {
    if (!selectedLetter || !selectedSecondLetter) {
      setAvailableThirdLetters([]);
      setSelectedThirdLetter('');
      setAvailableFourthLetters([]);
      setSelectedFourthLetter('');
      return;
    }

    const thirds = sortLetters(Object.keys(thirdCounts[selectedLetter]?.[selectedSecondLetter] || {}));
    setAvailableThirdLetters(thirds);
    if (selectedThirdLetter && !thirds.includes(selectedThirdLetter)) {
      setSelectedThirdLetter('');
      setAvailableFourthLetters([]);
      setSelectedFourthLetter('');
    }
  }, [selectedLetter, selectedSecondLetter, thirdCounts, selectedThirdLetter, sortLetters]);

  useEffect(() => {
    if (!selectedLetter || !selectedSecondLetter || !selectedThirdLetter) {
      setAvailableFourthLetters([]);
      setSelectedFourthLetter('');
      return;
    }

    const fourths = sortLetters(Object.keys(fourthCounts[selectedLetter]?.[selectedSecondLetter]?.[selectedThirdLetter] || {}));
    setAvailableFourthLetters(fourths);
    if (selectedFourthLetter && !fourths.includes(selectedFourthLetter)) {
      setSelectedFourthLetter('');
    }
  }, [selectedLetter, selectedSecondLetter, selectedThirdLetter, fourthCounts, selectedFourthLetter, sortLetters]);
  
  const letterFilteredWells = useMemo(() => {
    return filteredByMeta.filter((well) => {
      const name = getWellNameGeneric(well).toUpperCase();
      const first = getLetterKey(name);
      if (selectedLetter && first !== selectedLetter) return false;

      if (selectedSecondLetter) {
        if (first === '#') return false;
        const second = getLetterKey(name.charAt(1));
        if (second !== selectedSecondLetter) return false;
      }

      if (selectedThirdLetter) {
        const third = getLetterKey(name.charAt(2));
        if (third !== selectedThirdLetter) return false;
      }

      if (selectedFourthLetter) {
        const fourth = getLetterKey(name.charAt(3));
        if (fourth !== selectedFourthLetter) return false;
      }

      return true;
    });
  }, [filteredByMeta, selectedLetter, selectedSecondLetter, selectedThirdLetter, selectedFourthLetter]);

  const dynamicRowHeight = useDynamicRowHeight({
    defaultRowHeight: ITEM_HEIGHT,
    key: `${query}-${sortOrder}-${letterFilteredWells.length}-${selectedLetter}-${selectedSecondLetter}-${selectedThirdLetter}-${selectedFourthLetter}`
  });
  
  // Get unique values from filtered wells (status/county/operator only)
  const uniqueStatuses = Array.from(new Set(filteredByMeta.map(well => well.status).filter(Boolean)));
  const uniqueCounties = Array.from(new Set(filteredByMeta.map(well => well.county).filter(Boolean)));
  const uniqueOperators = Array.from(new Set(filteredByMeta.map(well => well.operator).filter(Boolean)));
  
  // Count active filters
  const activeFiltersCount = [statusFilter, countyFilter, operatorFilter].filter(f => f !== 'all').length;
  
  // Status filter handlers
  const handleStatusFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusAnchorEl(event.currentTarget);
  };
  
  const handleStatusFilterClose = () => {
    setStatusAnchorEl(null);
  };
  
  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    handleStatusFilterClose();
  };
  
  // County filter handlers
  const handleCountyFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setCountyAnchorEl(event.currentTarget);
  };
  
  const handleCountyFilterClose = () => {
    setCountyAnchorEl(null);
  };
  
  const handleCountyFilterChange = (county: string) => {
    setCountyFilter(county);
    handleCountyFilterClose();
  };
  
  // Operator filter handlers
  const handleOperatorFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setOperatorAnchorEl(event.currentTarget);
  };
  
  const handleOperatorFilterClose = () => {
    setOperatorAnchorEl(null);
  };
  
  const handleOperatorFilterChange = (operator: string) => {
    setOperatorFilter(operator);
    handleOperatorFilterClose();
  };

  const handleLetterStageChange = useCallback((direction: number) => {
    const currentIndex = letterStages.indexOf(letterStage);
    const nextIndex = Math.min(maxLetterStageIndex, Math.max(0, currentIndex + direction));
    if (nextIndex !== currentIndex) {
      telegram.hapticFeedback('light');
      setLetterStage(letterStages[nextIndex]);
    }
  }, [letterStage, maxLetterStageIndex]);

  const letterBreadcrumb = useMemo(() => {
    const parts = [] as string[];
    if (selectedLetter) parts.push(selectedLetter);
    if (selectedSecondLetter) parts.push(selectedSecondLetter);
    if (selectedThirdLetter) parts.push(selectedThirdLetter);
    if (selectedFourthLetter) parts.push(selectedFourthLetter);
    if (parts.length === 0) return tr('all', 'All');
    return parts.join(' > ');
  }, [selectedLetter, selectedSecondLetter, selectedThirdLetter, selectedFourthLetter, tr]);

  const letterStageLabels = useMemo(() => ({
    first: tr('letters', 'Letters'),
    second: tr('secondLetters', 'Second Letter'),
    third: tr('thirdLetters', 'Third Letter'),
    fourth: tr('fourthLetters', 'Fourth Letter')
  }), [tr]);

  const renderLetterChips = () => {
    const chipRowSx = {
      display: 'flex',
      alignItems: 'center',
      gap: letterExpanded ? 0.5 : 0.35,
      overflowX: 'auto',
      mt: letterExpanded ? 0.45 : 0.25,
      py: letterExpanded ? 0.25 : 0.1,
      px: letterExpanded ? 0.25 : 0.1,
      scrollbarWidth: 'thin' as const,
      '&::-webkit-scrollbar': { height: 4 },
      '&::-webkit-scrollbar-thumb': { backgroundColor: `${cyber.neonBlue}30`, borderRadius: 8 }
    };

    const chipSx = (selected: boolean) => ({
      px: letterExpanded ? 1 : 0.7,
      py: letterExpanded ? 0.55 : 0.35,
      borderRadius: letterExpanded ? '12px' : '9px',
      border: `1px solid ${cyber.borderDefault}`,
      backgroundColor: selected ? `${cyber.neonBlue}15` : cyber.bgCard,
      color: selected ? cyber.neonBlue : cyber.textSecondary,
      display: 'inline-flex',
      alignItems: 'center',
      gap: letterExpanded ? 0.55 : 0.35,
      cursor: 'pointer',
      minHeight: letterExpanded ? 34 : 24,
      minWidth: letterExpanded ? 42 : 32,
      flexShrink: 0,
      transition: 'all 0.2s ease',
      '&:hover': { borderColor: cyber.borderHover }
    });

    if (letterStage === 'first') {
      return (
        <Box sx={chipRowSx}>
          <Box
            onClick={() => {
              telegram.hapticFeedback('light');
              setSelectedLetter('');
              setSelectedSecondLetter('');
              setSelectedThirdLetter('');
              setSelectedFourthLetter('');
              setLetterStage('first');
            }}
            sx={chipSx(selectedLetter === '')}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{tr('all', 'All')}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{filteredByMeta.length.toLocaleString()}</Typography>
          </Box>
          {availableLetters.map((letter) => (
            <Box
              key={letter}
              onClick={() => {
                telegram.hapticFeedback('light');
                setSelectedLetter(letter);
                setSelectedSecondLetter('');
                setSelectedThirdLetter('');
                setSelectedFourthLetter('');
                if (Object.keys(secondCounts[letter] || {}).length > 0) {
                  setLetterStage('second');
                }
              }}
              sx={chipSx(selectedLetter === letter)}
            >
              <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(letterCounts[letter] || 0).toLocaleString()}</Typography>
            </Box>
          ))}
        </Box>
      );
    }

    if (letterStage === 'second') {
      if (!selectedLetter) {
        return <Typography variant="caption" sx={{ color: cyber.textSecondary, display: 'block', mt: 0.5 }}>{t('selectALetter')}</Typography>;
      }

      return (
        <Box sx={chipRowSx}>
          <Box
            onClick={() => {
              telegram.hapticFeedback('light');
              setSelectedSecondLetter('');
              setSelectedThirdLetter('');
              setSelectedFourthLetter('');
              setLetterStage('second');
            }}
            sx={chipSx(selectedSecondLetter === '')}
          >
            <Typography sx={{ fontWeight: 700 }}>{`${tr('all', 'All')} ${selectedLetter}*`}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(letterCounts[selectedLetter] || 0).toLocaleString()}</Typography>
          </Box>
          {availableSecondLetters.map((letter) => (
            <Box
              key={letter}
              onClick={() => {
                telegram.hapticFeedback('light');
                setSelectedSecondLetter(letter);
                setSelectedThirdLetter('');
                setSelectedFourthLetter('');
                if (Object.keys(thirdCounts[selectedLetter]?.[letter] || {}).length > 0) {
                  setLetterStage('third');
                }
              }}
              sx={chipSx(selectedSecondLetter === letter)}
            >
              <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(secondCounts[selectedLetter]?.[letter] || 0).toLocaleString()}</Typography>
            </Box>
          ))}
        </Box>
      );
    }

    if (letterStage === 'third') {
      if (!selectedLetter || !selectedSecondLetter) {
        return <Typography variant="caption" sx={{ color: cyber.textSecondary, display: 'block', mt: 0.5 }}>{tr('selectSecondLetter', 'Pick a second letter to continue')}</Typography>;
      }

      return (
        <Box sx={chipRowSx}>
          <Box
            onClick={() => {
              telegram.hapticFeedback('light');
              setSelectedThirdLetter('');
              setSelectedFourthLetter('');
              setLetterStage('third');
            }}
            sx={chipSx(selectedThirdLetter === '')}
          >
            <Typography sx={{ fontWeight: 700 }}>{`${tr('all', 'All')} ${selectedLetter}${selectedSecondLetter}*`}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(secondCounts[selectedLetter]?.[selectedSecondLetter] || 0).toLocaleString()}</Typography>
          </Box>
          {availableThirdLetters.map((letter) => (
            <Box
              key={letter}
              onClick={() => {
                telegram.hapticFeedback('light');
                setSelectedThirdLetter(letter);
                setSelectedFourthLetter('');
                if (Object.keys(fourthCounts[selectedLetter]?.[selectedSecondLetter]?.[letter] || {}).length > 0) {
                  setLetterStage('fourth');
                }
              }}
              sx={chipSx(selectedThirdLetter === letter)}
            >
              <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(thirdCounts[selectedLetter]?.[selectedSecondLetter]?.[letter] || 0).toLocaleString()}</Typography>
            </Box>
          ))}
        </Box>
      );
    }

    if (!selectedLetter || !selectedSecondLetter || !selectedThirdLetter) {
      return <Typography variant="caption" sx={{ color: cyber.textSecondary, display: 'block', mt: 0.5 }}>{tr('selectThirdLetter', 'Pick a third letter to continue')}</Typography>;
      return <Typography variant="caption" sx={{ color: cyber.textSecondary, display: 'block', mt: 0.5 }}>{tr('selectThirdLetter', 'Pick a third letter to continue')}</Typography>;
    }

    return (
      <Box sx={chipRowSx}>
        <Box
          onClick={() => {
            telegram.hapticFeedback('light');
            setSelectedFourthLetter('');
            setLetterStage('fourth');
          }}
          sx={chipSx(selectedFourthLetter === '')}
        >
          <Typography sx={{ fontWeight: 700 }}>{`${tr('all', 'All')} ${selectedLetter}${selectedSecondLetter}${selectedThirdLetter}*`}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(thirdCounts[selectedLetter]?.[selectedSecondLetter]?.[selectedThirdLetter] || 0).toLocaleString()}</Typography>
        </Box>
        {availableFourthLetters.map((letter) => (
          <Box
            key={letter}
            onClick={() => {
              telegram.hapticFeedback('light');
              setSelectedFourthLetter(letter);
            }}
            sx={chipSx(selectedFourthLetter === letter)}
          >
            <Typography sx={{ fontWeight: 700 }}>{letter}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: cyber.textMuted }}>{(fourthCounts[selectedLetter]?.[selectedSecondLetter]?.[selectedThirdLetter]?.[letter] || 0).toLocaleString()}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const letterStageIndex = letterStages.indexOf(letterStage);
  
  // Group wells by first letter and create flat list with headers
  const { groupedItems, letterIndex, listAvailableLetters } = useMemo(() => {
    const items: ListItemData[] = [];
    const index: Record<string, number> = {}; // letter -> index in items array
    const letters: string[] = [];
    const countsByLetter: Record<string, number> = {};

    letterFilteredWells.forEach((well) => {
      const letter = getLetterKey(getWellNameGeneric(well));
      countsByLetter[letter] = (countsByLetter[letter] || 0) + 1;
    });
    
    let currentFirstLetter = '';
    
    letterFilteredWells.forEach((well) => {
      const letter = getLetterKey(getWellNameGeneric(well));
      
      if (letter !== currentFirstLetter) {
        const count = countsByLetter[letter] || 0;
        index[letter] = items.length;
        letters.push(letter);
        items.push({ type: 'header', letter, count });
        currentFirstLetter = letter;
      }
      
      items.push({ type: 'well', well });
    });
    
    return { groupedItems: items, letterIndex: index, listAvailableLetters: letters };
  }, [letterFilteredWells]);
  
  // Handle visible rows change to update current letter badge and trigger infinite loading
  const handleRowsRendered = useCallback((visibleRows: { startIndex: number; stopIndex: number }) => {
    // Find which letter section the first visible row belongs to
    let foundLetter = '';
    
    // Look backward from startIndex to find the most recent header
    for (let i = visibleRows.startIndex; i >= 0; i--) {
      if (groupedItems[i]?.type === 'header') {
        foundLetter = (groupedItems[i] as { type: 'header'; letter: string; count: number }).letter;
        break;
      }
    }
    
    if (!foundLetter && listAvailableLetters.length > 0) {
      foundLetter = listAvailableLetters[0];
    }

    if (!foundLetter && selectedLetter) {
      foundLetter = selectedLetter;
    }
    
    if (foundLetter && foundLetter !== currentLetter) {
      setCurrentLetter(foundLetter);
      setShowLetterBadge(true);
      
      // Hide badge after 1.5 seconds of no scrolling
      if (badgeTimeoutRef.current) {
        clearTimeout(badgeTimeoutRef.current);
      }
      badgeTimeoutRef.current = setTimeout(() => {
        setShowLetterBadge(false);
      }, 1500);
    }
    
    // Trigger infinite scroll - load more when near the end
    if (hasMore && !loadingMore && onLoadMore) {
      const threshold = 20; // Load more when within 20 items of the end
      if (visibleRows.stopIndex >= groupedItems.length - threshold) {
        onLoadMore();
      }
    }
  }, [groupedItems, listAvailableLetters, currentLetter, selectedLetter, hasMore, loadingMore, onLoadMore]);
  
  // Jump to a specific letter
  const handleLetterClick = useCallback((letter: string) => {
    const idx = letterIndex[letter];
    if (idx !== undefined && listRef.current) {
      listRef.current.scrollToRow({ index: idx, align: 'start' });
      setCurrentLetter(letter);
      setShowLetterBadge(true);
      
      // Haptic feedback
      telegram.hapticFeedback('light');
      
      // Hide badge after 1.5 seconds
      if (badgeTimeoutRef.current) {
        clearTimeout(badgeTimeoutRef.current);
      }
      badgeTimeoutRef.current = setTimeout(() => {
        setShowLetterBadge(false);
      }, 1500);
    }
  }, [letterIndex, listRef]);

    const handleShare = useCallback((well: Well | any) => {
      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}`;
      const shareText = `🛢️ ${well.wellName}\nAPI: ${well.api || well.identifier || ''}\n${well.operator ? `Operator: ${well.operator}\n` : ''}${well.county ? `County: ${well.county}\n` : ''}\n📍 Get Directions:\n${mapsUrl}\n\n📱 Find more: @ziawellnavbot`;
      telegram.webApp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(mapsUrl)}&text=${encodeURIComponent(shareText)}`);
    }, []);

    // Open Google Maps with driving directions
    const handleGoogleMaps = useCallback((well: Well | any) => {
      telegram.hapticFeedback('light');
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${well.latitude},${well.longitude}&travelmode=driving`, '_blank');
    }, []);

    // Apple Maps and Waze removed - only Google Maps supports waypoints for caliche routes
  
  // Render a single row (header or well)
  // v2 react-window passes ariaAttributes, index, style plus any rowProps
  const Row = useCallback((props: { index: number; style: React.CSSProperties }) => {
    const { index, style } = props;
    const item = groupedItems[index];
    
    if (item.type === 'header') {
      // Section header
      return (
        <Box
          style={style}
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            backgroundColor: cyber.bgCard,
            borderBottom: `1px solid ${cyber.borderDefault}`,
            backdropFilter: 'blur(8px)'
          }}
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: cyber.neonBlue, 
              fontWeight: 700,
              fontSize: '1rem'
            }}
          >
            {item.letter}
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: cyber.textSecondary, 
              ml: 1.5,
              fontSize: '0.75rem'
            }}
          >
            {item.count.toLocaleString()} {item.count === 1 ? t('result') : t('results')}
          </Typography>
        </Box>
      );
    }
    
    // Well card
    const well = item.well;
    const favorited = isFavorite(well.id);
    
    // Get facility type color for card border/glow (matching quick search buttons)
    const facilityColor = getFacilityTypeColor((well as any).type, (well as any).facilityType, well.wellName);
    // Get status colors for the status badge only
    const statusColors = getStatusColors(well.status);
    const statusLower = (well.status || '').toLowerCase();
    const isActiveStatus = statusLower.includes('active') || statusLower.includes('producing') || statusLower.includes('drilling') || statusLower.includes('completed');
    
    return (
      <Box style={style} sx={{ px: 1, py: 0.5 }}>
        <Box 
          sx={{
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
            borderRadius: '12px',
            boxShadow: `0 6px 16px ${facilityColor}25, 0 4px 12px rgba(0,0,0,0.12)`,
            px: { xs: 1, sm: 1.5 },
            pt: { xs: 0.75, sm: 1 },
            pb: { xs: 1.25, sm: 1.5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            transform: 'scale(1)',
            '&:hover': {
              transform: { xs: 'scale(1)', sm: 'scale(1.02)' },
              boxShadow: `0 12px 28px ${facilityColor}40, 0 8px 20px rgba(0,0,0,0.18)`,
            }
          }}
        >
          {/* ROW 1: Icon + Facility Name */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.9, sm: 1.4 }, minWidth: 0 }}>
            <FacilityIcon type={(well as any).type} facilityType={(well as any).facilityType} name={well.wellName} size={32} />
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontWeight: 700, 
                color: cyber.textPrimary,
                fontSize: { xs: '1.05rem', sm: '1.15rem' },
                lineHeight: 1.35,
                flex: 1,
                whiteSpace: 'normal',
                wordBreak: 'break-word'
              }}
            >
              {well.wellName}
            </Typography>
          </Box>

          {/* ROW 2: API, Lat/Long, County, Operator, State */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 0.5, sm: 0.75 }, 
            rowGap: { xs: 0.6, sm: 0.75 },
            flexWrap: 'wrap', 
            alignItems: 'flex-start',
            pb: 0.75,
          }}>
            {well.api && (
              <Chip
                label={well.api}
                size="small"
                icon={<ContentCopyIcon sx={{ fontSize: '0.75rem !important' }} />}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  navigator.clipboard.writeText(well.api).then(() => {
                    telegram.hapticFeedback('success');
                    setToast({ open: true, message: t('apiCopied', well.api) });
                  });
                }}
                sx={{
                  height: 24,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.15)' : 'rgba(30, 64, 175, 0.1)',
                  color: cyber.chipBlue,
                  cursor: 'pointer',
                  '& .MuiChip-icon': { color: cyber.chipBlue },
                  '& .MuiChip-label': { px: 0.75 },
                  '&:hover': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.3)' : 'rgba(30, 64, 175, 0.2)' }
                }}
              />
            )}
            {well.latitude && well.longitude && (
              <Chip
                label={`${well.latitude.toFixed(4)}, ${well.longitude.toFixed(4)}`}
                size="small"
                icon={<LocationOnIcon sx={{ fontSize: '0.85rem !important' }} />}
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${well.latitude},${well.longitude}`;
                  window.open(mapsUrl, '_blank');
                }}
                sx={{
                  height: 24,
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(180, 83, 9, 0.1)',
                  color: darkMode ? '#fbbf24' : '#b45309',
                  cursor: 'pointer',
                  '& .MuiChip-icon': { color: darkMode ? '#fbbf24' : '#b45309' },
                  '& .MuiChip-label': { px: 0.75 },
                  '&:hover': { backgroundColor: darkMode ? 'rgba(251, 191, 36, 0.3)' : 'rgba(180, 83, 9, 0.2)' }
                }}
              />
            )}
            <Chip 
              label={well.county} 
              size="small" 
              sx={{ 
                backgroundColor: darkMode ? 'rgba(52, 211, 153, 0.1)' : 'rgba(6, 95, 70, 0.1)',
                color: cyber.chipGreen,
                fontSize: '0.82rem',
                fontWeight: 600,
                height: 24,
                fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                '& .MuiChip-label': { px: 0.75 }
              }}
            />
            {/* State Badge (TX/NM) */}
            {well.state && (
              <Chip
                label={well.state}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                  backgroundColor: well.state === 'TX' 
                    ? (darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(127, 29, 29, 0.15)')
                    : (darkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(30, 64, 175, 0.15)'),
                  color: well.state === 'TX' 
                    ? (darkMode ? '#ef4444' : '#b91c1c')
                    : (darkMode ? '#3b82f6' : '#1e40af'),
                  border: `1px solid ${well.state === 'TX' 
                    ? (darkMode ? '#ef4444' : '#dc2626')
                    : (darkMode ? '#3b82f6' : '#1d4ed8')
                  }`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              />
            )}
            <Chip 
              label={well.operator} 
              size="small" 
              sx={{ 
                backgroundColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                color: darkMode ? '#ffffff' : '#334155',
                fontSize: '0.82rem',
                fontWeight: 600,
                height: 24,
                fontFamily: '"Google Sans Flex", system-ui, sans-serif',
                '& .MuiChip-label': { px: 0.75, whiteSpace: 'normal' }
              }}
            />
            {!isActiveStatus && well.status && (
              <Box sx={{
                backgroundColor: statusColors.bg,
                border: `1px solid ${statusColors.border}`,
                borderRadius: '6px',
                px: 0.5,
                py: 0.3,
                display: 'flex',
                alignItems: 'center',
                gap: 0.35
              }}>
                <Box sx={{
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  backgroundColor: statusColors.dot,
                  boxShadow: statusColors.dotGlow
                }} />
                <Typography variant="caption" sx={{ 
                  color: statusColors.text, 
                  fontWeight: 700, 
                  fontSize: '0.78rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  fontFamily: '"Google Sans Flex", system-ui, sans-serif'
                }}>
                  {well.status}
                </Typography>
              </Box>
            )}
          </Box>

          {/* ROW 3: Action Buttons - Left aligned under badges */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start', 
            gap: { xs: 0.25, sm: 0.5 }, 
            mt: 0.5, 
            pt: 0.5,
            overflowX: 'auto',
            flexWrap: 'wrap'
          }}>
            {/* Favorite */}
            <Tooltip title={favorited ? t('removeFromFavoritesTooltip') : t('addToFavoritesTooltip')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  telegram.hapticFeedback('light');
                  onToggleFavorite(well);
                }}
                sx={{
                  width: { xs: 44, sm: 48 },
                  height: { xs: 44, sm: 48 },
                  color: favorited ? cyber.neonGold : cyber.textMuted,
                  backgroundColor: favorited ? 'rgba(251, 191, 36, 0.12)' : 'transparent',
                  '&:hover': { color: cyber.neonGold, backgroundColor: 'rgba(251, 191, 36, 0.2)' }
                }}
              >
                {favorited ? <StarIcon sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' } }} /> : <StarBorderIcon sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' } }} />}
              </IconButton>
            </Tooltip>

            {/* View on Map */}
            <Tooltip title={t('viewOnMapTooltip')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  telegram.hapticFeedback('light');
                  onClose();
                  onViewOnMap(well);
                }}
                sx={{
                  width: { xs: 44, sm: 48 },
                  height: { xs: 44, sm: 48 },
                  color: cyber.neonBlue,
                  backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.12)' : 'rgba(30, 64, 175, 0.1)',
                  '&:hover': { backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(30, 64, 175, 0.2)' }
                }}
            >
                <MapIcon sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' } }} />
              </IconButton>
            </Tooltip>

            {/* Share */}
            <Tooltip title={t('shareTooltip')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleShare(well);
                }}
                sx={{
                  width: { xs: 44, sm: 48 },
                  height: { xs: 44, sm: 48 },
                  color: darkMode ? '#22c55e' : '#16a34a',
                  backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.12)' : 'rgba(34, 197, 94, 0.1)',
                  '&:hover': { backgroundColor: darkMode ? 'rgba(34, 197, 94, 0.22)' : 'rgba(34, 197, 94, 0.18)' }
                }}
              >
                <ShareIcon sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' } }} />
              </IconButton>
            </Tooltip>

            {/* Google Maps */}
            <Tooltip title={t('googleTooltip')} arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoogleMaps(well);
                }}
                sx={{
                  width: { xs: 44, sm: 48 },
                  height: { xs: 44, sm: 48 },
                  color: '#4285F4',
                  backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.15)' : 'rgba(66, 133, 244, 0.1)',
                  '&:hover': { backgroundColor: darkMode ? 'rgba(66, 133, 244, 0.3)' : 'rgba(66, 133, 244, 0.2)' }
                }}
              >
                <GoogleIcon sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' } }} />
              </IconButton>
            </Tooltip>

            {/* Photo Gallery */}
            <Tooltip title={t('photoGalleryTooltip')} arrow>
              <IconButton
                size="small"
                onClick={(e) => handleOpenGallery(well, e)}
                sx={{
                  width: { xs: 44, sm: 48 },
                  height: { xs: 44, sm: 48 },
                  color: cyber.neonPink,
                  backgroundColor: `${cyber.neonPink}20`,
                  '&:hover': { 
                    backgroundColor: `${cyber.neonPink}40`,
                    boxShadow: `0 0 12px ${cyber.neonPink}60`
                  }
                }}
              >
                <PhotoLibraryIcon sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' } }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    );
  }, [groupedItems, isFavorite, onSelectWell, onViewOnMap, onToggleFavorite, handleOpenGallery, cyber.neonPink]);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: cyber.bgCard,
          backgroundImage: 'none',
          height: 'calc(100vh - 100px)',
          maxHeight: 'calc(100vh - 100px)',
          width: '95vw',
          maxWidth: '900px',
          m: 1,
          mb: '96px', // Account for bottom FAB bar
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ color: cyber.textPrimary, p: 1.5, pb: 1, flexShrink: 0 }}>
        {/* Compact Header - Search Info + Filters in ONE row */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          gap: 1,
          pb: 1,
          borderBottom: `1px solid ${cyber.borderDefault}`
        }}>
          {/* Left: Search info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: cyber.textSecondary, fontSize: '0.75rem', display: 'block', lineHeight: 1.3 }}>
              <span style={{ color: cyber.neonBlue, fontWeight: 600 }}>"{query}"</span> • <span style={{ color: cyber.neonBlue, fontWeight: 600 }}>{wells.length.toLocaleString()}</span>{totalCount && totalCount > wells.length ? <span>/{totalCount.toLocaleString()}</span> : ''}
              {cacheInfo && (
                <span style={{ color: cacheInfo.cached ? cyber.neonGreen : cyber.neonGold, marginLeft: '6px' }}>
                  ⚡{cacheInfo.time}ms
                </span>
              )}
            </Typography>
            {activeFiltersCount > 0 && (
              <Typography variant="caption" sx={{ color: cyber.neonGreen, fontSize: '0.7rem', fontWeight: 600 }}>
                {activeFiltersCount} filter{activeFiltersCount > 1 ? 's' : ''}: {[
                  statusFilter !== 'all' && statusFilter,
                  countyFilter !== 'all' && countyFilter,
                  operatorFilter !== 'all' && operatorFilter
                ].filter(Boolean).join(' • ')}
              </Typography>
            )}
          </Box>
          {/* Right: Compact Filter Buttons */}
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            {/* Status Filter */}
            <Tooltip title={t('filterByStatus')}>
              <IconButton 
                onClick={handleStatusFilterClick}
                sx={{ 
                  width: 36,
                  height: 36,
                  color: statusFilter !== 'all' ? cyber.neonGreen : cyber.textMuted,
                  backgroundColor: statusFilter !== 'all' ? `${cyber.neonGreen}15` : 'transparent',
                  '&:hover': { backgroundColor: `${cyber.neonBlue}15` }
                }}
              >
                <FilterListIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* County Filter */}
            <Tooltip title={t('filterByCounty')}>
              <IconButton 
                onClick={handleCountyFilterClick}
                sx={{ 
                  width: 36,
                  height: 36,
                  color: countyFilter !== 'all' ? cyber.neonGreen : cyber.textMuted,
                  backgroundColor: countyFilter !== 'all' ? `${cyber.neonGreen}15` : 'transparent',
                  '&:hover': { backgroundColor: `${cyber.neonBlue}15` }
                }}
              >
                <LocationOnIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* Operator Filter */}
            <Tooltip title={t('filterByOperator')}>
              <IconButton 
                onClick={handleOperatorFilterClick}
                sx={{ 
                  width: 36,
                  height: 36,
                  color: operatorFilter !== 'all' ? cyber.neonGreen : cyber.textMuted,
                  backgroundColor: operatorFilter !== 'all' ? `${cyber.neonGreen}15` : 'transparent',
                  '&:hover': { backgroundColor: `${cyber.neonBlue}15` }
                }}
              >
                <BusinessIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>

            {/* Sort Button */}
            <Tooltip title={sortOrder === 'asc' ? t('sortZA') : t('sortAZ')}>
              <IconButton 
                onClick={onToggleSort}
                sx={{ 
                  width: 36,
                  height: 36,
                  color: cyber.textMuted,
                  '&:hover': { backgroundColor: `${cyber.neonBlue}15`, color: cyber.neonBlue }
                }}
              >
                <SortByAlphaIcon sx={{ 
                  fontSize: 18,
                  transform: sortOrder === 'desc' ? 'scaleY(-1)' : 'none',
                  transition: 'transform 0.2s ease'
                }} />
              </IconButton>
            </Tooltip>

            {/* Close Button */}
            <Tooltip title={t('close')}>
              <IconButton 
                onClick={() => { telegram.hapticFeedback('light'); onClose(); }}
                sx={{ 
                  width: 36,
                  height: 36,
                  color: cyber.neonPink,
                  '&:hover': { backgroundColor: `${cyber.neonPink}15` }
                }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      {/* Status Filter Menu */}
      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={handleStatusFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${cyber.borderDefault}`,
            boxShadow: darkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <MenuItem 
          onClick={() => handleStatusFilterChange('all')}
          selected={statusFilter === 'all'}
          sx={{ 
            color: cyber.textPrimary,
            '&.Mui-selected': {
              backgroundColor: `${cyber.neonBlue}20`,
              '&:hover': { backgroundColor: `${cyber.neonBlue}30` }
            },
            '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          {t('allStatus')} ({filteredByMeta.length})
        </MenuItem>
        {uniqueStatuses.sort().map((status) => {
          const statusColor = (() => {
            const s = (status || '').toLowerCase();
            // Red for closed/inactive/cancelled - check these FIRST (highest priority)
            if (s.includes('inactive') || s.includes('closure approved') || s.includes('closed') || 
                s.includes('abandoned') || s.includes('plugged') || s.includes('cancelled')) return '#ef4444';
            // Amber for pending/requests/not accepted
            if (s.includes('pending') || s.includes('request') || s.includes('submission') || 
                s.includes('upcoming') || s.includes('not accepted') || s.includes('closure not')) return '#f59e0b';
            // Green for active/approved/accepted/completed (check AFTER red/amber)
            if (s.includes('active') || s.includes('producing') || s.includes('approved') || 
                s.includes('accepted') || s.includes('completed') || s.includes('registration')) return '#10b981';
            return cyber.neonBlue;
          })();
          return (
            <MenuItem 
              key={status}
              onClick={() => handleStatusFilterChange(status!)}
              selected={statusFilter === status}
              sx={{ 
                color: statusColor,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&.Mui-selected': {
                  backgroundColor: `${statusColor}20`,
                  '&:hover': { backgroundColor: `${statusColor}30` }
                },
                '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }} />
              {status} ({filteredByMeta.filter(w => w.status === status).length})
            </MenuItem>
          );
        })}
      </Menu>

      {/* County Filter Menu */}
      <Menu
        anchorEl={countyAnchorEl}
        open={Boolean(countyAnchorEl)}
        onClose={handleCountyFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${cyber.borderDefault}`,
            boxShadow: darkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
            maxHeight: '400px'
          }
        }}
      >
        <MenuItem 
          onClick={() => handleCountyFilterChange('all')}
          selected={countyFilter === 'all'}
          sx={{ 
            color: cyber.textPrimary,
            '&.Mui-selected': {
              backgroundColor: `${cyber.neonBlue}20`,
              '&:hover': { backgroundColor: `${cyber.neonBlue}30` }
            },
            '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          {t('allCounties')} ({filteredByMeta.length})
        </MenuItem>
        {uniqueCounties.sort().map((county) => (
          <MenuItem 
            key={county}
            onClick={() => handleCountyFilterChange(county!)}
            selected={countyFilter === county}
            sx={{ 
              color: cyber.textPrimary,
              '&.Mui-selected': {
                backgroundColor: `${cyber.neonBlue}20`,
                '&:hover': { backgroundColor: `${cyber.neonBlue}30` }
              },
              '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            {county} ({filteredByMeta.filter(w => w.county === county).length})
          </MenuItem>
        ))}
      </Menu>

      {/* Operator Filter Menu */}
      <Menu
        anchorEl={operatorAnchorEl}
        open={Boolean(operatorAnchorEl)}
        onClose={handleOperatorFilterClose}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#1e293b' : '#ffffff',
            border: `1px solid ${cyber.borderDefault}`,
            boxShadow: darkMode ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.15)',
            maxHeight: '400px'
          }
        }}
      >
        <MenuItem 
          onClick={() => handleOperatorFilterChange('all')}
          selected={operatorFilter === 'all'}
          sx={{ 
            color: cyber.textPrimary,
            '&.Mui-selected': {
              backgroundColor: `${cyber.neonBlue}20`,
              '&:hover': { backgroundColor: `${cyber.neonBlue}30` }
            },
            '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
          }}
        >
          {t('allOperators')} ({filteredByMeta.length})
        </MenuItem>
        {uniqueOperators.sort().map((operator) => (
          <MenuItem 
            key={operator}
            onClick={() => handleOperatorFilterChange(operator!)}
            selected={operatorFilter === operator}
            sx={{ 
              color: cyber.textPrimary,
              '&.Mui-selected': {
                backgroundColor: `${cyber.neonBlue}20`,
                '&:hover': { backgroundColor: `${cyber.neonBlue}30` }
              },
              '&:hover': { backgroundColor: darkMode ? 'rgba(148, 163, 184, 0.1)' : 'rgba(0, 0, 0, 0.04)' }
            }}
          >
            {operator} ({filteredByMeta.filter(w => w.operator === operator).length})
          </MenuItem>
        ))}
      </Menu>

      {/* Letter Drilldown - Compact with Glove-Friendly Chevrons */}
      <Box sx={{ px: 1.5, py: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: `1px solid ${cyber.borderDefault}` }}>
        {/* Left: Breadcrumb + Count + Clear */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: cyber.textSecondary, fontWeight: 700, fontSize: '0.75rem' }}>
              {letterBreadcrumb}
            </Typography>
            <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.7rem', ml: 1 }}>
              {letterFilteredWells.length.toLocaleString()}/{filteredByMeta.length.toLocaleString()}
            </Typography>
          </Box>
          
          {/* Clear Button - appears when any letter is selected */}
          {selectedLetter && (
            <IconButton
              onClick={() => {
                telegram.hapticFeedback('medium');
                setSelectedLetter('');
                setSelectedSecondLetter('');
                setSelectedThirdLetter('');
                setSelectedFourthLetter('');
                setLetterStage('first');
              }}
              sx={{
                width: 36,
                height: 36,
                color: '#f97316',
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                border: '2px solid rgba(249, 115, 22, 0.5)',
                borderRadius: 2,
                '&:hover': { backgroundColor: 'rgba(249, 115, 22, 0.2)' },
                '&:active': { transform: 'scale(0.95)' }
              }}
            >
              <ClearAllIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Box>
        
        {/* Right: Glove-Friendly Navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Expand/Collapse */}
          <IconButton
            onClick={() => setLetterExpanded((v) => !v)}
            sx={{ 
              width: 44, 
              height: 44, 
              color: letterExpanded ? cyber.neonBlue : cyber.textSecondary,
              backgroundColor: letterExpanded ? `${cyber.neonBlue}15` : 'transparent',
              border: `2px solid ${letterExpanded ? cyber.neonBlue : cyber.borderDefault}`,
              borderRadius: 2,
              '&:hover': { backgroundColor: `${cyber.neonBlue}20` }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 24, transform: letterExpanded ? 'rotate(-90deg)' : 'rotate(90deg)', transition: 'transform 0.2s' }} />
          </IconButton>
          
          {/* Previous Stage */}
          <IconButton
            onClick={() => handleLetterStageChange(-1)}
            disabled={letterStageIndex === 0}
            sx={{ 
              width: 52, 
              height: 52, 
              color: letterStageIndex === 0 ? cyber.textMuted : cyber.neonCyan,
              backgroundColor: letterStageIndex === 0 ? 'transparent' : `${cyber.neonCyan}10`,
              border: `2px solid ${letterStageIndex === 0 ? cyber.borderDefault : cyber.neonCyan}`,
              borderRadius: 2,
              '&:hover:not(:disabled)': { backgroundColor: `${cyber.neonCyan}25` },
              '&:active:not(:disabled)': { transform: 'scale(0.95)' }
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 28 }} />
          </IconButton>
          
          {/* Current Stage Label */}
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              border: `2px solid ${cyber.neonBlue}`,
              backgroundColor: `${cyber.neonBlue}15`,
              color: cyber.neonBlue,
              minWidth: 70,
              textAlign: 'center'
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{letterStageLabels[letterStage]}</Typography>
          </Box>
          
          {/* Next Stage */}
          <IconButton
            onClick={() => handleLetterStageChange(1)}
            disabled={letterStageIndex >= maxLetterStageIndex}
            sx={{ 
              width: 52, 
              height: 52, 
              color: letterStageIndex >= maxLetterStageIndex ? cyber.textMuted : cyber.neonCyan,
              backgroundColor: letterStageIndex >= maxLetterStageIndex ? 'transparent' : `${cyber.neonCyan}10`,
              border: `2px solid ${letterStageIndex >= maxLetterStageIndex ? cyber.borderDefault : cyber.neonCyan}`,
              borderRadius: 2,
              '&:hover:not(:disabled)': { backgroundColor: `${cyber.neonCyan}25` },
              '&:active:not(:disabled)': { transform: 'scale(0.95)' }
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>
      
      {/* Expanded Letter Chips */}
      {letterExpanded && (
        <Box sx={{ px: 1.5, py: 0.75, borderBottom: `1px solid ${cyber.borderDefault}` }}>
          {renderLetterChips()}
        </Box>
      )}

      {/* Main Content - Virtualized List */}
      <DialogContent 
        sx={{ 
          p: 0, 
          flex: 1, 
          display: 'flex', 
          position: 'relative', 
          overflow: 'hidden',
          minHeight: 0
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <CyberSpinner size={48} />
          </Box>
        ) : letterFilteredWells.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, px: 3, flex: 1 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('noTypeFound', facilityTypeName || t('searchResults'))}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeFiltersCount > 0
                ? t('noFilteredResults')
                : t('tryDifferentSearch')
              }
            </Typography>
          </Box>
        ) : (
          <>
            {/* Virtualized List */}
            <Box 
              ref={containerRef}
              sx={{
                flex: 1,
                pr: { xs: '68px', sm: '88px' },
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <List<Record<string, never>>
                listRef={listRef}
                rowComponent={Row}
                rowCount={groupedItems.length}
                rowHeight={dynamicRowHeight}
                rowProps={{}}
                onRowsRendered={handleRowsRendered}
                style={{ 
                  height: listHeight || 500,
                  width: '100%',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  scrollbarColor: darkMode ? 'rgba(96, 165, 250, 0.5) transparent' : 'rgba(100, 116, 139, 0.5) transparent'
                }}
              />
              {/* Loading More Indicator */}
              {loadingMore && (
                <Box sx={{ 
                  position: 'absolute', 
                  bottom: 8, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: 'rgba(15, 23, 42, 0.95)',
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  border: `1px solid ${cyber.neonBlue}40`,
                  boxShadow: `0 4px 12px rgba(0,0,0,0.3)`
                }}>
                  <CyberSpinner size={20} />
                  <Typography sx={{ color: cyber.neonBlue, fontSize: '0.875rem' }}>{t('loadingMore')}</Typography>
                </Box>
              )}
            </Box>
            
            {/* Floating Letter Badge - appears when scrolling */}
            {showLetterBadge && currentLetter && (
              <Box
                sx={{
                  position: 'absolute',
                  right: { xs: 70, sm: 98 },
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 64,
                  height: 64,
                  borderRadius: '16px',
                  backgroundColor: 'rgba(96, 165, 250, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  zIndex: 100,
                  pointerEvents: 'none',
                  animation: 'fadeIn 0.15s ease-out',
                  '@keyframes fadeIn': {
                    from: { opacity: 0, transform: 'translateY(-50%) scale(0.8)' },
                    to: { opacity: 1, transform: 'translateY(-50%) scale(1)' }
                  }
                }}
              >
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '2rem' }}>
                  {currentLetter}
                </Typography>
              </Box>
            )}
            
            {/* Alphabet Scrubber - Right Side */}
            <Box
              sx={{
                position: 'absolute',
                right: 4,
                top: 8,
                bottom: 8,
                width: 44,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 0,
                zIndex: 50,
                backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                borderRadius: '16px',
                py: 1,
                backdropFilter: 'blur(8px)',
                border: darkMode ? '1px solid rgba(96, 165, 250, 0.3)' : '1px solid rgba(100, 116, 139, 0.3)',
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' }
              }}
            >
              {listAvailableLetters.map((letter) => (
                <Box
                  key={letter}
                  onClick={() => handleLetterClick(letter)}
                  sx={{
                    width: 40,
                    height: 36,
                    minHeight: 36,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: currentLetter === letter 
                      ? (darkMode ? '#60a5fa' : '#1e40af') 
                      : (darkMode ? '#94a3b8' : '#64748b'),
                    fontWeight: currentLetter === letter ? 700 : 600,
                    fontSize: '1rem',
                    transition: 'all 0.15s ease',
                    borderRadius: '8px',
                    flexShrink: 0,
                    backgroundColor: currentLetter === letter 
                      ? (darkMode ? 'rgba(96, 165, 250, 0.2)' : 'rgba(30, 64, 175, 0.15)') 
                      : 'transparent',
                    '&:hover': {
                      color: darkMode ? '#60a5fa' : '#1e40af',
                      backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.25)' : 'rgba(30, 64, 175, 0.15)'
                    },
                    '&:active': {
                      backgroundColor: darkMode ? 'rgba(96, 165, 250, 0.5)' : 'rgba(30, 64, 175, 0.3)',
                      transform: 'scale(1.15)'
                    }
                  }}
                >
                  {letter}
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      {wells.length > 0 && (
        <DialogActions sx={{ 
          p: 0.75, 
          backgroundColor: darkMode ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 0.95)', 
          flexShrink: 0,
          borderTop: `1px solid ${cyber.borderDefault}`,
          minHeight: 40
        }}>
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: cyber.textMuted, fontSize: '0.7rem' }}>
              {t('shownOfTotal', letterFilteredWells.length.toLocaleString(), filteredByMeta.length.toLocaleString())}
            </Typography>
            <Button 
              onClick={onClose} 
              size="small"
              sx={{ 
                color: cyber.neonCyan, 
                fontSize: '0.75rem',
                py: 0.25,
                minHeight: 28
              }}
            >
              {t('close')}
            </Button>
          </Box>
        </DialogActions>
      )}

      {/* Toast for copy feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={2000}
        onClose={() => setToast({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ marginTop: '60px' }}
      >
        <Alert severity="success" sx={{ backgroundColor: 'rgba(34, 197, 94, 0.9)', color: 'white' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Photo Gallery Dialog */}
      <Dialog
        open={galleryOpen}
        onClose={() => {
          setGalleryOpen(false);
          setGalleryFacility(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: cyber.bgCard,
            backgroundImage: 'none',
            maxHeight: '80vh',
            m: 1,
            borderRadius: 2,
            border: `1px solid ${cyber.neonPink}40`
          }
        }}
      >
        <DialogTitle sx={{ 
          color: cyber.textPrimary, 
          pb: 1, 
          pt: 1.5, 
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${cyber.borderDefault}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoLibraryIcon sx={{ color: cyber.neonPink, fontSize: '1.25rem' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: cyber.textPrimary }}>
              📷 {t('facilityPhotos', galleryFacility?.wellName || t('facility'))}
            </Typography>
          </Box>
          <IconButton 
            size="small" 
            onClick={() => {
              setGalleryOpen(false);
              setGalleryFacility(null);
            }}
            sx={{ color: cyber.textSecondary }}
          >
            <CloseIcon sx={{ fontSize: '1.1rem' }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto' }}>
          {galleryFacility && (
            <PhotoGallery
              facilityId={galleryFacility.id}
              facilityType={galleryFacility.type || galleryFacility.facilityType || 'well'}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
